/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['modalContent', 'modalOverlay'] }] */
import { Component } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import cn from 'classnames';

import { pageTypeChange } from 'actions/common';
import { setFederatedLoginModalVisibility } from 'actions/headerfooter';
import { CART_PAGE_MODAL } from 'constants/amethystPageTypes';
import { HYDRA_DOUBLE_PRODUCT_REMOVE, HYDRA_FEDERATED_LOGIN } from 'constants/hydraTests';
import { onCartModalClose, onCartView, onViewCartClick } from 'store/ducks/cart/actions';
import { isAssigned, triggerAssignment } from 'actions/ab';
import {
  fetchCartItems,
  navigateToCheckout,
  showCartModal
} from 'actions/cart';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import JanusPixel from 'components/common/JanusPixel';
import { ensureNoClass, pluralize } from 'helpers';
import { toUSD } from 'helpers/NumberFormats';
import { groupCartItemsBySizePrediction } from 'helpers/CartUtils';
import { isDesktop } from 'helpers/ClientUtils';
import { onEvent } from 'helpers/EventHelpers';
import FocusTrap from 'components/common/FocusTrap';
import { SmallLoader } from 'components/Loader';
import CartItem from 'components/cart/CartItem';
import EmptyCart from 'components/cart/EmptyCart';
import CartErrors from 'components/cart/CartErrors';
import RewardsTransparency from 'components/common/RewardsTransparency';
import marketplace from 'cfg/marketplace.json';

import modalCSS from 'styles/components/common/modal.scss';
import css from 'styles/components/cart/cartModal.scss';

const { checkout: { checkoutUrl }, cart: { cartName } } = marketplace;

export class CartModal extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    router: PropTypes.object.isRequired,
    marketplace: PropTypes.object
  };

  state = {};

  componentDidMount() {
    const {
      cart: { cartObj: cart, isLoaded, activeItems },
      fetchCartItems,
      showCartModal,
      onCartView,
      pageTypeChange,
      sourcePageType,
      triggerAssignment
    } = this.props;

    this.setState({ sourcePageType });

    const { itemsWithFitNotRecommended } = groupCartItemsBySizePrediction(activeItems);
    itemsWithFitNotRecommended.length && triggerAssignment(HYDRA_DOUBLE_PRODUCT_REMOVE);

    onEvent(window, 'keydown', this.onEscKeyDown, undefined, this);

    pageTypeChange('cartModal');

    if (!isLoaded) {
      fetchCartItems().then(response => {
        onCartView({ ...response, isCartModal: true });
      });
    } else {
      onCartView({ ...cart, isCartModal: true });
    }

    if (!isDesktop()) {
      setTimeout(() => {
        showCartModal(false);
      }, 5000);
    } else {
      this.toggleDesktopModal();
    }
  }

  componentDidUpdate(prevProps) {
    const { cart: { cartObj: { activeItems } } } = this.props;
    const { cart: { cartObj: { activeItems: prevActiveItems } }, triggerAssignment } = prevProps;
    if (activeItems !== prevActiveItems) {
      const { itemsWithFitNotRecommended } = groupCartItemsBySizePrediction(activeItems);
      itemsWithFitNotRecommended.length && triggerAssignment(HYDRA_DOUBLE_PRODUCT_REMOVE);
    }
  }

  componentWillUnmount() {
    ensureNoClass(document.documentElement, 'scrollLock');
  }

  onEscKeyDown = e => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      this.onHideModal();
    }
  };

  // When cart modal is used like an actual modal on desktop, lock scroll of the body and throw aria-hidden on the root element
  // This is skipped on mobile when it is used more as a sticky header confirmation
  toggleDesktopModal = () => {
    document.documentElement.classList.toggle('scrollLock');
    const root = document.getElementById('root');
    if (root.hasAttribute('aria-hidden')) {
      root.removeAttribute('aria-hidden');
    } else {
      root.setAttribute('aria-hidden', true);
    }
  };

  onHideModal = () => {
    const { onCartModalClose, pageTypeChange, showCartModal } = this.props;
    const { sourcePageType } = this.state;
    this.toggleDesktopModal();

    pageTypeChange(sourcePageType);
    showCartModal(false);
    onCartModalClose();
  };

  onClickedViewCart = () => {
    this.props.onViewCartClick();
    this.onHideModal();
  };

  onCheckingOut = e => {
    e.preventDefault();

    const { navigateToCheckout } = this.props;
    const { router } = this.context;

    // order below matters, as hiding modal will update pageType before the redirect logic can grab it from state
    navigateToCheckout(router, CART_PAGE_MODAL);
    this.onHideModal();
  };

  handleFederatedSignIn = e => {
    e.preventDefault();
    const { setFederatedLoginModalVisibility, showCartModal } = this.props;
    showCartModal(false);
    setFederatedLoginModalVisibility(true, {
      returnTo: encodeURIComponent(location.pathname)
    });
  };

  makeEmptyCart = () => {
    const { isCustomer, hydraFederatedLogin } = this.props;
    const { testId } = this.context;

    return (
      <EmptyCart
        onLinkClick={this.onHideModal}
        className={css.empty}
        isCustomer={isCustomer}
        handleFederatedSignIn={this.handleFederatedSignIn}
        hydraFederatedLogin={hydraFederatedLogin}
      >
        { !isCustomer &&
          <div className={css.signIn}>
            <Link to={hydraFederatedLogin ? '/federated-login' : '/login'} onClick={hydraFederatedLogin && this.handleFederatedSignIn} data-test-id={testId('signInButton')}>Sign In</Link>
          </div>
        }
      </EmptyCart>
    );
  };

  makeCartItems = () => {
    const { hydraDoubleProductRemove, cart: { cartObj: { activeItems, savedItems } } } = this.props;
    const { marketplace: { cart: { showFitRecommendations } } } = this.context;

    // Create three sets of cartItems, those with both correct and incorrect size predictions, and those without
    const { itemsWithFitRecommended, itemsWithFitNotRecommended, itemsWithNoRecommendationAvailable } = groupCartItemsBySizePrediction(activeItems);
    const numberOfNotRecommended = itemsWithFitNotRecommended.length;
    return (
      <>
        <div className={css.itemContainer}>
          <CartErrors isModal={true} />
          {showFitRecommendations
            ?
              <>
                {Boolean(numberOfNotRecommended) &&
                  <div className={css.fitNotRecommended}>
                    <p className={css.fitCallout}>{numberOfNotRecommended === 1 ? 'This' : 'These'} {pluralize('size', numberOfNotRecommended)} might not fit you!</p>
                    <p>Our handy-dandy sizing feature suggests that {pluralize('this', numberOfNotRecommended)} {pluralize('size', numberOfNotRecommended)} might not be the best fit for you.</p>
                    {itemsWithFitNotRecommended.map(item =>
                      <CartItem
                        className={css.item}
                        item={item}
                        isModal={true}
                        key={item.cartItemId}
                        allowAdjustQuantity={false}
                        isNotRecommendedFit={true}
                        forceRemoveButton={true}
                        hydraDoubleProductRemove={hydraDoubleProductRemove}
                      />
                    )}
                  </div>
                }
                {itemsWithFitRecommended.map(item =>
                  <CartItem
                    className={css.item}
                    item={item}
                    isModal={true}
                    isRecommendedFit={true}
                    key={item.cartItemId}
                  />
                )}
                {itemsWithNoRecommendationAvailable.map(item =>
                  <CartItem
                    className={css.item}
                    item={item}
                    isModal={true}
                    key={item.cartItemId}
                  />
                )}
              </>
            :
              <>
                {activeItems.map(item => {
                  const { cartItemId } = item;
                  return <CartItem
                    className={css.item}
                    item={item}
                    isModal={true}
                    key={cartItemId} />;
                })}
              </>
          }
          {savedItems?.map(item =>
            <CartItem
              className={css.item}
              item={item}
              isModal={true}
              isUnavailable={true}
              key={item.cartItemId} />
          )}
        </div>

        { this.makeCartFooter() }
      </>
    );
  };

  makeHeader = () => {
    const { cart: { addedStockId } } = this.props;
    const { testId } = this.context;

    return (
      <p className={css.header} id="modalDescription" data-test-id={testId('cartModalHeader')}>
        { addedStockId
          ? <><span className={css.check} /> Added to {cartName}</>
          : `My ${cartName}`
        }
      </p>
    );
  };

  makeCartFooter = () => {
    const { cart: { cartCount, loading, cartObj: { subtotal } } } = this.props;
    const { testId } = this.context;

    return (
      <div className={css.footer}>
        <p className={css.subtotal}>
          <span data-test-id={testId('cartQuantity')}>{cartName} Subtotal ({cartCount} {pluralize('item', cartCount)})</span>
          <span data-test-id={testId('subtotal')}>{toUSD(subtotal.amount)}</span>
        </p>

        <form onSubmit={this.onCheckingOut} action={`${checkoutUrl}/initiate`} method="post">
          <Link
            to="/cart"
            onClick={this.onClickedViewCart}
            className={css.secondaryCTA}
            data-test-id={testId('viewCart')}>View {cartName}</Link>

          <button
            type="submit"
            disabled={loading}
            className={css.primaryCTA}
            data-test-id={testId('proceedToCheckout')}>{ loading ? 'Updating...' : 'Proceed to Checkout'}</button>
        </form>
      </div>
    );
  };

  makeJanusPixel = () => {
    const { cart: { addedStockId, cartObj: cart } } = this.props;
    const addedProduct = cart.activeItems?.find(item => item.stockId === addedStockId);

    if (!addedProduct) {
      return null;
    }

    const { stockId, styleId } = addedProduct;

    const queryParams = {
      teen: styleId,
      child: stockId,
      widget: 'RecordCart'
    };

    return <JanusPixel queryParams={queryParams} />;
  };

  render() {
    const { cart: { isLoaded, isModalShowing, addedStockId, cartObj: cart } } = this.props;
    const { activeItems } = cart;
    const { testId } = this.context;

    if (!isModalShowing) {
      return null;
    }

    return createPortal(
      <FocusTrap active={isModalShowing} shouldFocusFirstElement>
        {focusRef => (
          <>
          <div
            className={css.container}
            role="dialog"
            aria-modal="true"
            aria-describedby="modalDescription"
            ref={focusRef}
          >
            { addedStockId && this.makeJanusPixel() }
            <div className={css.modal} data-test-id={testId('cartModal')}>
              <div className={cn(modalCSS.header, css.modalHeader)}>
                { this.makeHeader() }
                <button
                  type="button"
                  onClick={this.onHideModal}
                  className={modalCSS.close}
                  data-test-id={testId('closeModal')}
                  aria-label="Close"/>
              </div>
              {
                !isLoaded
                  ? <SmallLoader />
                  : <>
              <RewardsTransparency isForCartModal={true} />
                {
                  !activeItems.length
                    ? this.makeEmptyCart()
                    : this.makeCartItems()
                }
              </>
              }
            </div>
          </div>
        {/* eslint-disable jsx-a11y/no-static-element-interactions */}
        {/*
          react-modal does onclick on divs
          https://github.com/reactjs/react-modal/blob/master/src/components/ModalPortal.js
        */}
        <div onClick={this.onHideModal} onKeyPress={this.onHideModal} className={css.wrapper} />
        </>
        )}
      </FocusTrap>,
      document.body
    );

  }
}

export function mapStateToProps(state) {
  const { cart, cookies, pageView: { pageType: sourcePageType } } = state;
  const isCustomer = !!cookies['x-main'];

  return {
    cart,
    isCustomer,
    hydraDoubleProductRemove: isAssigned(HYDRA_DOUBLE_PRODUCT_REMOVE, 1, state),
    hydraFederatedLogin: isAssigned(HYDRA_FEDERATED_LOGIN, 1, state),
    sourcePageType
  };
}

const ConnectedCartModal = connect(mapStateToProps, {
  fetchCartItems,
  showCartModal,
  navigateToCheckout,
  onCartView,
  onCartModalClose,
  onViewCartClick,
  pageTypeChange,
  setFederatedLoginModalVisibility,
  triggerAssignment
})(CartModal);

export default withErrorBoundary('CartModal', ConnectedCartModal);
